import React, { useState } from 'react';
import { ArrowLeft, RefreshCw, Zap, FileText, Sparkles } from 'lucide-react';
import { R_STRATEGIES } from '../../constants';
import { generateAssetPassport, downloadAssetReport } from '../../services/api';

const DashboardAssetDetail = ({ asset, onBack, assets = [], onSelectAsset, onReloadAssets }) => {
  // Hooks must be declared unconditionally at top-level
  const [genLoading, setGenLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  if (!asset) {
    // Render the overview placeholder and High-Potential list inside the same card
    const computePotentialProfit = (a) => {
      const strategyKey = a.current_strategy || 'R7';
      const strat = R_STRATEGIES[strategyKey] || {};
      const baseCostFactor = Number(strat.baseCostFactor || 0);
      const baseValueFactor = Number(strat.baseValueFactor || 0.7);
      const initial = Number(a.initial_value || a.current_market_value || 0);
      const cost = initial * baseCostFactor;
      const value = initial * baseValueFactor;
      return Math.round(value - cost);
    };

    const highPotentialAssets = (assets || [])
      .map(a => ({ ...a, _potentialProfit: computePotentialProfit(a) }))
      .filter(a => Number(a._potentialProfit || 0) > 2000)
      .sort((a, b) => Number(b._potentialProfit || 0) - Number(a._potentialProfit || 0))
      .slice(0, 5);

    return (
      <div className="bg-white p-6 rounded-xl shadow-xl border-t-4 border-gray-300">
        <p className="text-xl font-semibold text-gray-700">Select an Asset from the list to view detailed Circular Economy metrics.</p>
        <p className="text-sm text-gray-500 mt-2">Click on any asset card in the Asset Manager view or the list below to analyze its potential.</p>

        <h4 className="text-xl font-bold text-gray-800 mt-6 mb-3 border-b pb-2 flex items-center">
          <Zap className="w-5 h-5 mr-2 text-yellow-500" /> High-Potential Assets
        </h4>
        <div className="rounded-xl overflow-hidden border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Strategy', 'Condition', 'Potential Profit', 'Action'].map((header) => (
                  <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {highPotentialAssets.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    No assets with potential profit greater than $2000 found
                  </td>
                </tr>
              ) : (
                highPotentialAssets.map((a) => {
                  const strategyKey = a.current_strategy || 'R7';
                  const strategy = R_STRATEGIES[strategyKey] || { name: strategyKey, color: 'bg-gray-500' };
                  return (
                    <tr key={a.id} className="hover:bg-cyan-50 cursor-pointer" onClick={() => onSelectAsset && onSelectAsset(a.id)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{a.name} ({a.id})</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${strategy.color?.replace('bg-', 'bg-opacity-20 text-') || 'bg-gray-500'}`}>{strategy.name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{Number(a.condition_rating || 0).toFixed(1)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">${Number(a._potentialProfit || 0).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <span className="text-cyan-600 hover:text-cyan-900">View Details</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Prefer backend field current_strategy; fallback to R7
  const strategyKey = asset.current_strategy || 'R7';
  const strategy = R_STRATEGIES[strategyKey] || { name: strategyKey, color: 'bg-gray-500', desc: '' };

  // Safe numeric conversions
  const initialValue = Number(asset.initial_value || 0);
  const currentValue = Number(asset.current_market_value || asset.current_value || 0);
  const circularityScore = Number(asset.circularity_score || 0);

  // Compute potential profit based on current strategy factors
  const baseCostFactor = Number((R_STRATEGIES[strategyKey] && R_STRATEGIES[strategyKey].baseCostFactor) || 0);
  const baseValueFactor = Number((R_STRATEGIES[strategyKey] && R_STRATEGIES[strategyKey].baseValueFactor) || 0.7);
  const profitInitial = initialValue || currentValue;
  const potentialProfit = Math.round((profitInitial * baseValueFactor) - (profitInitial * baseCostFactor));

  const doGeneratePassport = async () => {
    try {
      setGenLoading(true);
      await generateAssetPassport(asset.id);
      alert('Passport generated successfully');
      if (onReloadAssets) onReloadAssets();
    } catch (e) {
      alert('Failed to generate passport');
    } finally {
      setGenLoading(false);
    }
  };

  const doDownloadReport = async () => {
    try {
      setReportLoading(true);
      const blob = await downloadAssetReport(asset.id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `asset_${asset.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to download report');
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-xl border-t-4 border-cyan-500">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center text-cyan-600 hover:text-cyan-800 transition">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Overview
        </button>
        <div className="flex gap-2">
          <button onClick={doGeneratePassport} disabled={genLoading} className={`inline-flex items-center px-3 py-2 rounded-md text-white ${genLoading ? 'bg-cyan-300' : 'bg-cyan-600 hover:bg-cyan-700'}`}>
            <Sparkles className="w-4 h-4 mr-1" /> {genLoading ? 'Generating…' : 'Generate Passport'}
          </button>
          {/* Report downloads are available in the Triage & Risk Analysis page */}
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-800 mb-4">{asset.name} <span className="text-sm font-mono text-gray-500 ml-2">({asset.id})</span></h3>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <p className="font-medium">Type:</p><p>{asset.asset_type || asset.assetType}</p>
        <p className="font-medium">Initial Value:</p><p>${initialValue.toLocaleString()}</p>
        <p className="font-medium">Current Market Value:</p><p>${currentValue.toLocaleString()}</p>
        <p className="font-medium">Last Service:</p><p>{asset.last_service || asset.lastService || '—'}</p>
      </div>

      <div className="mt-6 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
        <h4 className="text-lg font-bold text-cyan-800 flex items-center"><RefreshCw className="w-5 h-5 mr-2" /> Circular Economy Potential</h4>
        <div className="mt-2 grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600">Circularity Score</p>
            <p className="text-2xl font-bold text-cyan-600">{circularityScore.toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Current Strategy</p>
            <p className={`text-sm font-bold text-white px-2 py-1 rounded ${strategy.color}`}>{strategy.name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Potential Profit</p>
            <p className="text-2xl font-bold text-green-600">${Number(potentialProfit || 0).toLocaleString()}</p>
          </div>
        </div>
        {strategy.desc && <p className="mt-3 text-xs text-gray-700">{strategy.desc}</p>}
      </div>
    </div>
  );
};

export default DashboardAssetDetail;
